import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { PTPData } from '../types/ptp';
import { StepIndicator } from '../components/ui/StepIndicator';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { NewPTPScreen } from '../components/screens/NewPTPScreen';
import { VoiceDescriptionScreen } from '../components/screens/VoiceDescriptionScreen';
import { TaskUnderstandingScreen } from '../components/screens/TaskUnderstandingScreen';
import { WorkEnvironmentScreen } from '../components/screens/WorkEnvironmentScreen';
import { CrewAssignmentScreen } from '../components/screens/CrewAssignmentScreen';
import { HazardIdentificationScreen } from '../components/screens/HazardIdentificationScreen';
import { RiskEvaluationScreen } from '../components/screens/RiskEvaluationScreen';
import { ControlMeasuresScreen } from '../components/screens/ControlMeasuresScreen';
import { EquipmentSafetyScreen } from '../components/screens/EquipmentSafetyScreen';
import { PTPSummaryScreen } from '../components/screens/PTPSummaryScreen';
import { Screen11_PTPCreated } from '../components/screens/Screen11_PTPCreated';
import { Screen12_Recording } from '../components/screens/Screen12_Recording';
import { Screen13_RecordingComplete } from '../components/screens/Screen13_RecordingComplete';
import { Screen14_QualityScore } from '../components/screens/Screen14_QualityScore';
import { Screen15_NewInfo } from '../components/screens/Screen15_NewInfo';
import { Screen16_UpdatedPTP } from '../components/screens/Screen16_UpdatedPTP';
import { Screen17_FullReview } from '../components/screens/Screen17_FullReview';
import { Screen18_SendQuiz } from '../components/screens/Screen18_SendQuiz';
import { Screen19_ForemanQuiz } from '../components/screens/Screen19_ForemanQuiz';
import { Screen18_CrewQuizResults } from '../components/screens/Screen18_CrewQuizResults';
import { Screen21_PTPList } from '../components/screens/Screen21_PTPList';
import { PTPExportView } from '../components/PTPExportView';
const INITIAL_DATA: PTPData = {
  project: 'Downtown Office Tower',
  location: 'Level 2 Structural Frame',
  date: 'June 12, 2026',
  taskDescription: '',
  steps: [{
    description: 'Deliver steel beams to lift zone',
    hazards: [{
      hazard: 'Struck-by moving equipment',
      controls: ['Spotter for delivery vehicle', 'Establish designated staging zone'],
      riskLevel: 'MEDIUM'
    }]
  }, {
    description: 'Position mobile crane and deploy outriggers',
    hazards: [{
      hazard: 'Crane tip-over / unstable ground',
      controls: ['Verify ground conditions and compaction', 'Use outrigger pads on firm surface', 'Level crane before operation'],
      riskLevel: 'HIGH'
    }]
  }, {
    description: 'Inspect rigging equipment',
    hazards: [{
      hazard: 'Defective rigging failure under load',
      controls: ['Inspect all slings and shackles before use', 'Remove and tag defective equipment', 'Use only certified rigging rated for load'],
      riskLevel: 'HIGH'
    }]
  }, {
    description: 'Attach rigging to steel beam',
    hazards: [{
      hazard: 'Pinch points and hand injuries',
      controls: ['Keep hands clear of pinch points', 'Use tag lines to position rigging', 'Wear work gloves'],
      riskLevel: 'MEDIUM'
    }]
  }, {
    description: 'Lift beam to second floor structure',
    hazards: [{
      hazard: 'Suspended load hazards',
      controls: ['Establish crane exclusion zone', 'Use tag lines to control load movement', 'Designate trained signal person'],
      riskLevel: 'HIGH'
    }]
  }, {
    description: 'Guide beam into structural position',
    hazards: [{
      hazard: 'Working at height',
      controls: ['100% tie-off required', 'Guardrails at open perimeter'],
      riskLevel: 'HIGH'
    }, {
      hazard: 'Struck-by swinging load',
      controls: ['Signal person controls approach', 'Workers positioned clear of swing radius'],
      riskLevel: 'HIGH'
    }]
  }, {
    description: 'Bolt beam into place',
    hazards: [{
      hazard: 'Dropped objects',
      controls: ['Tool lanyards on all hand tools', 'Barricade area directly below work'],
      riskLevel: 'MEDIUM'
    }, {
      hazard: 'Working at height',
      controls: ['Fall protection required', 'Secured work platform or scaffolding'],
      riskLevel: 'HIGH'
    }]
  }],
  jobSteps: ['Deliver steel beams to lift zone', 'Position mobile crane and deploy outriggers', 'Inspect rigging equipment', 'Attach rigging to steel beam', 'Lift beam to second floor structure', 'Guide beam into structural position', 'Bolt beam into place', 'Disconnect rigging'],
  environmentConditions: ['Active construction site', 'Elevated structural work (>6 ft)', 'Work near crane operation'],
  crewSize: 5,
  crewRoles: [{
    role: 'Foreman',
    responsibility: 'Task supervision and safety coordination'
  }, {
    role: 'Crane Operator',
    responsibility: 'Operate mobile crane'
  }, {
    role: 'Rigger',
    responsibility: 'Attach lifting rigging and guide load'
  }, {
    role: 'Ironworker',
    responsibility: 'Position and bolt beam'
  }, {
    role: 'Laborer',
    responsibility: 'Assist with tools and rigging'
  }],
  selectedHazards: ['Working at height', 'Suspended load hazards', 'Struck-by hazards', 'Dropped objects'],
  riskAssessments: [{
    hazard: 'Suspended load hazards',
    likelihood: 'Possible',
    severity: 'Fatality potential',
    riskLevel: 'HIGH'
  }],
  engineeringControls: ['Establish crane exclusion zone', 'Use tag lines to control load movement'],
  adminControls: ['Designate trained signal person', 'Conduct pre-lift meeting'],
  ppeControls: ['Hard hat', 'High visibility vest'],
  equipment: ['Mobile crane', 'Rigging slings', 'Lifting shackles', 'Tag lines'],
  safetyChecks: ['Crane inspection completed', 'Rigging inspection completed', 'Weather conditions acceptable', 'Communication signals confirmed', 'Work area barricaded']
};
const TOTAL_STEPS = 22;
const STEP_LABELS = ['PTP List', 'New PTP', 'Voice Description', 'Task Steps', 'Environment', 'Crew', 'Hazards', 'Risk Assessment', 'Controls', 'Safety Checks', 'Summary', 'PTP Created', 'Recording', 'Analysis', 'Quality Score', 'New Info', 'Updated PTP', 'Meeting Review', 'Send Quiz', 'Foreman Quiz', 'Crew Readiness', 'PTP List'];
// Transitions that simulate server processing
// key = step you're leaving, value = { message, sub, duration ms }
const LOADING_TRANSITIONS: Record<number, {
  message: string;
  sub?: string;
  duration: number;
}> = {
  3: {
    message: 'Analyzing task description…',
    sub: 'AI is identifying job steps and hazards',
    duration: 2200
  },
  7: {
    message: 'Generating risk assessment…',
    sub: 'Evaluating likelihood and severity',
    duration: 1800
  },
  11: {
    message: 'Creating Pre-Task Plan…',
    sub: 'Compiling safety document',
    duration: 2400
  },
  14: {
    message: 'Analyzing meeting recording…',
    sub: 'Evaluating crew discussion quality',
    duration: 2200
  },
  15: {
    message: 'Extracting new information…',
    sub: 'Comparing meeting notes to PTP',
    duration: 1600
  },
  16: {
    message: 'Updating Pre-Task Plan…',
    sub: 'Incorporating meeting findings',
    duration: 1800
  },
  21: {
    message: 'Finalizing Pre-Task Plan…',
    sub: 'Saving to project records',
    duration: 1200
  }
};
export function PTPApp() {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState<PTPData>(INITIAL_DATA);
  const [showExport, setShowExport] = useState(false);
  const [exportIsUpdated, setExportIsUpdated] = useState(false);
  const [loading, setLoading] = useState<{
    message: string;
    sub?: string;
  } | null>(null);
  const updateData = (updates: Partial<PTPData>) => {
    setData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const advanceStep = useCallback((toStep: number, dir: number) => {
    setDirection(dir);
    setCurrentStep(toStep);
  }, []);
  const handleNextWithLoading = useCallback((fromStep: number, toStep: number, dir: number) => {
    const transition = LOADING_TRANSITIONS[fromStep];
    if (transition) {
      setLoading({
        message: transition.message,
        sub: transition.sub
      });
      setTimeout(() => {
        setLoading(null);
        advanceStep(toStep, dir);
      }, transition.duration);
    } else {
      advanceStep(toStep, dir);
    }
  }, [advanceStep]);
  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      handleNextWithLoading(currentStep, currentStep + 1, 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleExport = (isUpdated = false) => {
    setExportIsUpdated(isUpdated);
    setShowExport(true);
  };
  const screenProps = {
    data,
    updateData,
    onNext: handleNext,
    onBack: handleBack,
    onExport: () => handleExport(currentStep >= 17)
  };
  const renderScreen = () => {
    switch (currentStep) {
      case 1:
        return <Screen21_PTPList {...screenProps} data-id="element-1122" />;
      case 2:
        return <NewPTPScreen {...screenProps} data-id="element-1123" />;
      case 3:
        return <VoiceDescriptionScreen {...screenProps} data-id="element-1124" />;
      case 4:
        return <TaskUnderstandingScreen {...screenProps} data-id="element-1125" />;
      case 5:
        return <WorkEnvironmentScreen {...screenProps} data-id="element-1126" />;
      case 6:
        return <CrewAssignmentScreen {...screenProps} data-id="element-1127" />;
      case 7:
        return <HazardIdentificationScreen {...screenProps} data-id="element-1128" />;
      case 8:
        return <RiskEvaluationScreen {...screenProps} data-id="element-1129" />;
      case 9:
        return <ControlMeasuresScreen {...screenProps} data-id="element-1130" />;
      case 10:
        return <EquipmentSafetyScreen {...screenProps} data-id="element-1131" />;
      case 11:
        return <PTPSummaryScreen {...screenProps} data-id="element-1132" />;
      case 12:
        return <Screen11_PTPCreated {...screenProps} data-id="element-1133" />;
      case 13:
        return <Screen12_Recording {...screenProps} data-id="element-1134" />;
      case 14:
        return <Screen13_RecordingComplete {...screenProps} data-id="element-1135" />;
      case 15:
        return <Screen14_QualityScore {...screenProps} data-id="element-1136" />;
      case 16:
        return <Screen15_NewInfo {...screenProps} data-id="element-1137" />;
      case 17:
        return <Screen16_UpdatedPTP {...screenProps} onNext={() => {
          handleNextWithLoading(17, 19, 1);
        }} data-id="element-1138" />;
      case 18:
        return <Screen17_FullReview {...screenProps} data-id="element-1139" />;
      case 19:
        return <Screen18_SendQuiz {...screenProps} data-id="element-1140" />;
      case 20:
        return <Screen19_ForemanQuiz {...screenProps} data-id="element-1141" />;
      case 21:
        return <Screen18_CrewQuizResults {...screenProps} onNext={() => {
          handleNextWithLoading(21, 22, 1);
        }} onBack={() => {
          setDirection(-1);
          setCurrentStep(17);
        }} data-id="element-1142" />;
      case 22:
        return <Screen21_PTPList {...screenProps} showNewPTP onNext={() => {
          setDirection(1);
          setCurrentStep(2);
        }} data-id="element-1143" />;
      default:
        return null;
    }
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  return <div className="w-full h-full flex flex-col bg-slate-950 text-slate-200 p-6 overflow-hidden relative font-sans isolate" data-id="element-1144">
      {currentStep > 1 && currentStep < 22 && <button onClick={handleBack} className="flex items-center gap-1.5 text-slate-400 text-sm font-medium mb-3 -ml-1 self-start" data-id="element-1145">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1146" />
          Back
        </button>}
      <StepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} label={STEP_LABELS[currentStep - 1]} data-id="element-1147" />

      <div className="flex-1 relative w-full h-full" data-id="element-1148">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1149">
          <motion.div key={currentStep} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          ease: 'easeInOut',
          duration: 0.3
        }} className="absolute inset-0 w-full h-full" data-id="element-1150">
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1151">
        {loading && <LoadingOverlay message={loading.message} subMessage={loading.sub} data-id="element-1152" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-1153">
        {showExport && <PTPExportView data={data} onClose={() => setShowExport(false)} isUpdated={exportIsUpdated} data-id="element-1154" />}
      </AnimatePresence>
    </div>;
}